import type { CellClickedEvent, ICellRendererParams } from 'ag-grid-community'
import type { ColumnDefinition } from '@/components/atomic/table/types'

import { toError } from '@moment/observability/logger'
import { useState } from 'react'

import Icon from '@/components/atomic/icon'
import { IconName } from '@/components/atomic/icon/types'
import {
  BOND_STATUS_COLUMN,
  BOND_TYPE_COLUMN,
  BOOLEAN_COLUMN,
  COUPON_COLUMN,
  DATE_COLUMN,
  DECIMAL_COLUMN,
  DESCRIPTION_COLUMN,
  DURATION_COLUMN,
  defaultBondStatusRenderer,
  defaultBondTypeRenderer,
  defaultRatingRenderer,
  GAIN_LOSS_COLUMN,
  INTEGER_COLUMN,
  MONEY_COLUMN,
  RATING_COLUMN,
  TEXT_COLUMN,
  YIELD_COLUMN,
} from '@/components/atomic/table/columns'
import { COLUMN_WIDTH } from '@/components/atomic/table/constants'
import { TruncatedTextCell } from '@/components/atomic/truncated-text-cell'
import { AccountIdToggle } from '@/components/molecules/account-id-toggle'
import { globalLogger } from '@/lib/logger'
import { ColumnId } from '@/types/enums/column-id'
import { DateFormat } from '@/utils/dates/dateFormatting'
import { PLACEHOLDER } from '@/utils/presentation/formatting'
import { cn } from '@/utils/shadcn/utils'

import { ACCOUNTS_COLUMN_WIDTH_COLLAPSED } from './constants'

// import SellButtonRenderer from './sell-button-renderer'

const defaultCellRenderer = (params: ICellRendererParams) => {
  if (!params.valueFormatted) return null

  // Only show field for top-level rows
  const level = params.node.level
  if (level > 0) {
    return null // Hide for nested rows
  }

  return params.valueFormatted
}

/**
 * Reusable component for displaying count with optional expansion icon
 */
const CountWithExpansionIcon = ({
  count,
  label,
  isExpanded,
  showExpansionIcon,
}: {
  count: number
  label: string
  isExpanded: boolean
  showExpansionIcon: boolean
}) => {
  return (
    <div className="flex items-center">
      <span>{`${count} ${label}${count !== 1 ? 's' : ''}`}</span>
      {showExpansionIcon && (
        <Icon
          iconName={
            isExpanded ? IconName.ChevronDownIcon : IconName.ChevronRightIcon
          }
          className="ml-1 size-2"
        />
      )}
    </div>
  )
}

/**
 * Component for Level 1 (advisor level) that shows account count, expansion icon, and copy button
 */
const AccountLevelCellComponent = ({
  params,
}: {
  params: ICellRendererParams
}) => {
  const [copied, setCopied] = useState(false)
  const isExpanded = params.node.expanded

  // To copy account IDs as a bulk action for all accounts under the advisor
  const handleCopyAccountIds = async () => {
    try {
      const accountIds: string[] = []
      params.data.accounts?.forEach(
        (account: { custodian_account_id: string }) => {
          if (account.custodian_account_id) {
            accountIds.push(account.custodian_account_id)
          }
        },
      )

      if (accountIds.length === 0) {
        globalLogger.warn('No account IDs found to copy')
        return
      }

      // Copy to clipboard
      await navigator.clipboard.writeText(accountIds.join(', '))

      // Show success feedback
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      globalLogger.error('Failed to copy account IDs', {
        error: toError(error),
      })
    }
  }

  return (
    <div className="flex items-center">
      <CountWithExpansionIcon
        count={params.data.account_count}
        label="account"
        isExpanded={isExpanded}
        showExpansionIcon={!!params.node.group}
      />
      {isExpanded && (
        // biome-ignore lint/a11y/useSemanticElements: Bulk suppression as part of biome migration
        <div
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation()
            handleCopyAccountIds()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.stopPropagation()
              handleCopyAccountIds()
            }
          }}
          aria-label={copied ? 'Account IDs copied' : 'Copy account IDs'}
          className="ml-1"
        >
          <Icon
            iconName={
              copied ? IconName.CheckIcon : IconName.DocumentDuplicateIcon
            }
            outline
            // apply larger size and padding to the icon to make it easier to click
            className={cn(
              'copy-icon-in-holdings-table !p-2 !size-8 cursor-pointer hover:text-primary transition-colors',
              copied ? 'text-green-600' : 'text-muted-foreground',
            )}
          />
        </div>
      )}
    </div>
  )
}

const holdingsColumnDefinitions: Map<ColumnId, ColumnDefinition> = new Map([
  [
    ColumnId.ISIN,
    TEXT_COLUMN({
      headerName: 'ISIN',
      field: 'instrument.isin',
      colId: ColumnId.ISIN,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.DESCRIPTION,
    DESCRIPTION_COLUMN({
      headerName: 'Bond description',
      field: 'instrument.description',
      colId: ColumnId.DESCRIPTION,
      cellRenderer: (params: ICellRendererParams) => {
        return <TruncatedTextCell text={params.value} maxLength={40} />
      },
    }),
  ],
  [
    ColumnId.TYPE,
    // TODO: switch to ASSET_CLASS_COLUMN when this table needs to render equities or funds.
    BOND_TYPE_COLUMN({
      field: 'instrument.type',
      colId: ColumnId.TYPE,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.value) return null

        // Only show asset class for top-level rows
        const level = params.node.level
        if (level > 0) {
          return null // Hide for nested rows
        }

        return defaultBondTypeRenderer(params)
      },
    }),
  ],
  [
    ColumnId.ADVISORS,
    {
      headerName: 'Advisors',
      field: 'advisor_id',
      colId: ColumnId.ADVISORS,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data) return ''
        const level = params.node.level
        // Level 0 is instrument level - show count
        if (level === 0) {
          if (params.data.advisor_count <= 0) return PLACEHOLDER

          return (
            <CountWithExpansionIcon
              count={params.data.advisor_count}
              label="advisor"
              isExpanded={params.node.expanded}
              showExpansionIcon={!!params.node.group}
            />
          )
        }

        // Level 1 is advisor level - show advisor ID
        if (level === 1) {
          return (
            <TruncatedTextCell text={params.data.advisor_name || PLACEHOLDER} />
          )
        }

        // Level 2 is account level - don't show anything
        return ''
      },
      onCellClicked: (event: CellClickedEvent) => {
        if (event.node.group) {
          const isExpanded = event.node.expanded
          event.node.setExpanded(!isExpanded)
          // This is needed to refresh the row when the group is expanded/collapsed
          // Without this, the Icons don't update
          event.api.refreshCells({
            rowNodes: [event.node],
            columns: [event.column.getColId()],
            force: true,
          })
        }
      },
      minWidth: COLUMN_WIDTH.W_160,
      rowGroup: true,
    } as ColumnDefinition,
  ],
  [
    ColumnId.ACCOUNTS,
    {
      headerName: 'Accounts',
      field: 'account_id',
      colId: ColumnId.ACCOUNTS,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data) return ''
        const level = params.node.level

        // Level 0 is instrument level - show ONLY count
        if (level === 0) {
          if (params.data.account_count <= 0) return PLACEHOLDER

          return (
            <CountWithExpansionIcon
              count={params.data.account_count}
              label="account"
              isExpanded={params.node.expanded}
              showExpansionIcon={false}
            />
          )
        }

        // Level 1 is account summary level
        if (level === 1) {
          if (params.data.account_count <= 0) return PLACEHOLDER
          return <AccountLevelCellComponent params={params} />
        }

        // Level 2 is individual account level
        if (level === 2) {
          const accountName = params.data.account_name || PLACEHOLDER
          const custodianAccountId = params.data.custodian_account_id

          return (
            <div className="flex items-center gap-4">
              <TruncatedTextCell
                text={accountName}
                className={`w-[${ACCOUNTS_COLUMN_WIDTH_COLLAPSED}px]`}
              />
              {custodianAccountId && (
                <AccountIdToggle accountId={custodianAccountId} size="small" />
              )}
            </div>
          )
        }

        return ''
      },
      onCellClicked: (event: CellClickedEvent) => {
        if (event.node.group) {
          const isExpanded = event.node.expanded
          const target = event.event?.target as Element | null
          if (event.node.level === 1) {
            // If the target is not the copy icon, expand/collapse the node
            if (
              !target?.classList.value.includes('copy-icon-in-holdings-table')
            ) {
              event.node.setExpanded(!isExpanded)
            }
          } else {
            event.node.setExpanded(!isExpanded)
          }
          // This is needed to refresh the row when the group is expanded/collapsed
          // Without this, the Icons don't update
          event.api.refreshCells({
            rowNodes: [event.node],
            columns: [event.column.getColId()],
            force: true,
          })
        }
      },
      minWidth: ACCOUNTS_COLUMN_WIDTH_COLLAPSED,
      rowGroup: true,
    } as ColumnDefinition,
  ],
  [
    ColumnId.MARKET_VALUE,
    MONEY_COLUMN({
      field: 'market_value',
      headerName: 'Total value',
      colId: ColumnId.MARKET_VALUE,
    }),
  ],
  [
    ColumnId.PRINCIPAL_VALUE,
    MONEY_COLUMN({
      field: 'principal_value',
      headerName: 'Principal value',
      colId: ColumnId.PRINCIPAL_VALUE,
    }),
  ],
  [
    ColumnId.COST_BASIS,
    MONEY_COLUMN({
      field: 'cost_basis',
      headerName: 'Cost basis',
      colId: ColumnId.COST_BASIS,
    }),
  ],

  [
    ColumnId.GAIN_LOSS,
    GAIN_LOSS_COLUMN({
      headerName: 'Gain/loss',
      field: 'gain_loss',
      colId: ColumnId.GAIN_LOSS,
      hide: false,
    }),
  ],
  [
    ColumnId.MATURITY_IN_DAYS,
    INTEGER_COLUMN({
      headerName: 'Days to maturity',
      field: 'instrument.maturity_in_days',
      colId: ColumnId.MATURITY_IN_DAYS,
      minWidth: COLUMN_WIDTH.W_120,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  // New columns for additional instrument fields
  [
    ColumnId.ACCRUED_INTEREST,
    MONEY_COLUMN({
      headerName: 'Accrued Interest',
      field: 'instrument.accrued_interest',
      colId: ColumnId.ACCRUED_INTEREST,
      hide: true,
      minWidth: COLUMN_WIDTH.W_120,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.CALLABLE,
    BOOLEAN_COLUMN({
      headerName: 'Callable',
      field: 'instrument.callable',
      colId: ColumnId.CALLABLE,
      maxWidth: COLUMN_WIDTH.W_60,
      minWidth: COLUMN_WIDTH.W_60,
      hide: true,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.CLOSE_PRICE,
    MONEY_COLUMN({
      headerName: 'Close Price',
      field: 'instrument.close_price',
      colId: ColumnId.CLOSE_PRICE,
      hide: true,
      minWidth: COLUMN_WIDTH.W_120,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.CLOSE_YTM,
    YIELD_COLUMN({
      headerName: 'Close YTM',
      field: 'instrument.close_ytm',
      colId: ColumnId.CLOSE_YTM,
      hide: true,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.CLOSE_YTW,
    YIELD_COLUMN({
      headerName: 'Close YTW',
      field: 'instrument.close_ytw',
      colId: ColumnId.CLOSE_YTW,
      hide: true,
      minWidth: COLUMN_WIDTH.W_120,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.COUPON,
    COUPON_COLUMN({
      field: 'instrument.coupon',
      colId: ColumnId.COUPON,
      hide: true,
      minWidth: COLUMN_WIDTH.W_120,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.CUSIP,
    TEXT_COLUMN({
      headerName: 'CUSIP',
      field: 'instrument.cusip',
      colId: ColumnId.CUSIP,
      hide: true,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.DESCRIPTION_SHORT,
    DESCRIPTION_COLUMN({
      headerName: 'Short Description',
      field: 'instrument.description_short',
      colId: ColumnId.DESCRIPTION_SHORT,
      hide: true,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.DURATION,
    DURATION_COLUMN({
      field: 'instrument.duration',
      colId: ColumnId.DURATION,
      hide: true,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.ISSUER,
    TEXT_COLUMN({
      headerName: 'Issuer',
      field: 'instrument.issuer',
      minWidth: COLUMN_WIDTH.W_200,
      colId: ColumnId.ISSUER,
      hide: true,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.MATURITY_DATE,
    DATE_COLUMN(
      {
        headerName: 'Maturity Date',
        field: 'instrument.maturity_date',
        colId: ColumnId.MATURITY_DATE,
        hide: true,
        cellRenderer: defaultCellRenderer,
      },
      DateFormat.SHORT,
    ),
  ],
  [
    ColumnId.NEXT_CALL_DATE,
    DATE_COLUMN(
      {
        headerName: 'Next Call Date',
        field: 'instrument.next_call_date',
        colId: ColumnId.NEXT_CALL_DATE,
        hide: true,
        cellRenderer: defaultCellRenderer,
      },
      DateFormat.SHORT,
    ),
  ],
  [
    ColumnId.NEXT_CALL_PRICE,
    MONEY_COLUMN({
      headerName: 'Next Call Price',
      field: 'instrument.next_call_price',
      colId: ColumnId.NEXT_CALL_PRICE,
      hide: true,
      minWidth: COLUMN_WIDTH.W_120,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.PAR_VALUE,
    MONEY_COLUMN({
      headerName: 'Par Value',
      field: 'instrument.par_value',
      colId: ColumnId.PAR_VALUE,
      hide: true,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.PRICE,
    MONEY_COLUMN({
      headerName: 'Price',
      field: 'instrument.price',
      colId: ColumnId.PRICE,
      hide: true,
      minWidth: COLUMN_WIDTH.W_120,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.PRINCIPAL_FACTOR,
    DECIMAL_COLUMN({
      headerName: 'Principal Factor',
      field: 'instrument.principal_factor',
      colId: ColumnId.PRINCIPAL_FACTOR,
      hide: true,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.PURPOSE,
    TEXT_COLUMN({
      headerName: 'Purpose',
      field: 'instrument.purpose',
      minWidth: COLUMN_WIDTH.W_300,
      colId: ColumnId.PURPOSE,
      hide: true,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.NAICS_SECTOR,
    TEXT_COLUMN({
      headerName: 'Sector',
      field: 'instrument.naics_sector',
      minWidth: COLUMN_WIDTH.W_200,
      colId: ColumnId.NAICS_SECTOR,
      hide: true,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.SP_RATING,
    RATING_COLUMN({
      field: '', // Cell renderer responsible for accessing the correct field.
      minWidth: COLUMN_WIDTH.W_100,
      colId: ColumnId.SP_RATING,
      cellRenderer: (params: ICellRendererParams) => {
        // Only show rating for top-level rows
        if (params.node.level > 0) {
          return null // Hide for nested rows
        }

        // Pass both ratings to the renderer
        return defaultRatingRenderer({
          ...params,
          data: {
            ...params.data,
            sp_rating: params.data?.instrument?.sp_rating,
            moodys_rating: params.data?.instrument?.moodys_rating,
          },
        })
      },
    }),
  ],
  [
    ColumnId.STATUS,
    BOND_STATUS_COLUMN({
      headerName: 'Status',
      field: 'instrument.status',
      colId: ColumnId.STATUS,
      hide: true,
      maxWidth: COLUMN_WIDTH.W_120,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.value) return null

        // Only show field for top-level rows
        const level = params.node.level
        if (level > 0) {
          return null // Hide for nested rows
        }

        return defaultBondStatusRenderer(params)
      },
    }),
  ],
  [
    ColumnId.YTM,
    YIELD_COLUMN({
      headerName: 'YTM',
      field: 'instrument.ytm',
      colId: ColumnId.YTM,
      hide: true,
      maxWidth: COLUMN_WIDTH.W_100,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.YTW,
    YIELD_COLUMN({
      headerName: 'YTW',
      field: 'instrument.ytw',
      colId: ColumnId.YTW,
      hide: true,
      maxWidth: COLUMN_WIDTH.W_100,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.TICKER,
    TEXT_COLUMN({
      headerName: 'Ticker',
      field: 'instrument.ticker',
      colId: ColumnId.TICKER,
      hide: true,
      maxWidth: COLUMN_WIDTH.W_100,
      cellRenderer: defaultCellRenderer,
    }),
  ],
  [
    ColumnId.MONEY_MANAGER,
    TEXT_COLUMN({
      headerName: 'Money Manager',
      field: 'money_manager',
      colId: ColumnId.MONEY_MANAGER,
      minWidth: COLUMN_WIDTH.W_200,
      sortable: false,
      hide: false,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.value) return null

        // Only show money manager for account level rows (level 2)
        const level = params.node.level
        if (level !== 2) {
          return null
        }

        return <TruncatedTextCell text={params.value} />
      },
    }),
  ],
  // [
  //   ColumnId.SELL_BUTTON,
  //   {
  //     headerName: '',
  //     field: 'sell_button',
  //     colId: ColumnId.SELL_BUTTON,
  //     width: 60,
  //     minWidth: 60,
  //     maxWidth: 60,
  //     sortable: false,
  //     resizable: false,
  //     pinned: 'right',
  //     lockPinned: true,
  //     suppressMovable: true,
  //     cellRenderer: SellButtonRenderer,
  //     cellRendererParams: (params: ICellRendererParams) => ({
  //       onSellClick: params.context?.onSellClick,
  //     }),
  //     cellStyle: {
  //       '--pinned-cell-width': '60px',
  //       boxShadow: '-4px 0 6px -2px rgba(0, 0, 0, 0.1)',
  //     },
  //   } as ColumnDefinition,
  // ],
])

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default holdingsColumnDefinitions
